import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Analytics utilities (inline to avoid import issues during build)
const analytics = {
  trackEvent: (eventName: string, parameters?: Record<string, unknown>) => {
    if (typeof window === 'undefined' || !window.gtag) return;
    window.gtag('event', eventName, {
      event_category: 'engagement',
      event_label: parameters?.label,
      value: parameters?.value,
      ...parameters,
    });
  },

  trackAffiliateClick: (affiliate: string, position: string, page: string, additionalData?: {
    occupation?: string;
    location?: string;
    type?: string;
    affiliate_enabled?: boolean;
    conversion_value?: number;
  }) => {
    analytics.trackEvent('affiliate_click', {
      affiliate_name: affiliate,
      click_position: position,
      page_type: page,
      event_category: 'revenue',
      ...additionalData,
    });
  },

  trackSalaryLookup: (occupation: string, area: string, source: string) => {
    analytics.trackEvent('salary_lookup', {
      occupation_code: occupation,
      area_code: area,
      lookup_source: source,
      event_category: 'engagement',
    });
  },

  trackRppToggle: (enabled: boolean, page: string) => {
    analytics.trackEvent('rpp_toggle', {
      rpp_enabled: enabled,
      page_type: page,
      event_category: 'pro_features',
    });
  },

  trackCalculatorUsage: (hourlyRate: number, resultType: string) => {
    analytics.trackEvent('calculator_usage', {
      hourly_rate: hourlyRate,
      result_type: resultType,
      event_category: 'tools',
    });
  },

  trackSearch: (query: string, results: number, searchType: string) => {
    analytics.trackEvent('search', {
      search_term: query,
      results_count: results,
      search_type: searchType,
      event_category: 'engagement',
    });
  },

  trackNoDegreeView: (occupation: string, area: string) => {
    analytics.trackEvent('no_degree_view', {
      occupation_code: occupation,
      area_code: area,
      event_category: 'high_value_content',
    });
  },

  trackExport: (exportType: string, dataType: string, isPro: boolean) => {
    analytics.trackEvent('data_export', {
      export_format: exportType,
      data_type: dataType,
      is_pro_user: isPro,
      event_category: isPro ? 'pro_features' : 'engagement',
    });
  },

  trackAdView: (adSlot: string, page: string, position: string) => {
    analytics.trackEvent('ad_view', {
      ad_slot: adSlot,
      page_type: page,
      ad_position: position,
      event_category: 'monetization',
    });
  },

  trackPdfDownload: (occupation: string, area: string, isPro: boolean) => {
    analytics.trackEvent('pdf_download_click', {
      occupation_code: occupation,
      area_code: area,
      is_pro_user: isPro,
      event_category: 'revenue',
    });
  },

  trackCalcConvert: (fromType: string, toType: string, value: number) => {
    analytics.trackEvent('calc_convert', {
      from_type: fromType,
      to_type: toType,
      value: value,
      event_category: 'tools',
    });
  },

  trackTableSort: (tableType: string, column: string, direction: string) => {
    analytics.trackEvent('table_sort', {
      table_type: tableType,
      sort_column: column,
      sort_direction: direction,
      event_category: 'engagement',
    });
  },

  trackNavInternal: (from: string, to: string, linkType: string) => {
    analytics.trackEvent('nav_internal', {
      from_page: from,
      to_page: to,
      link_type: linkType,
      event_category: 'navigation',
    });
  },

  trackContactFormSubmit: (inquiryType: string) => {
    analytics.trackEvent('contact_form_submit', {
      inquiry_type: inquiryType,
      event_category: 'engagement',
    });
  },

  trackFAQClick: (question: string) => {
    analytics.trackEvent('faq_click', {
      question: question,
      event_category: 'support',
    });
  },

  trackFAQExpand: (question: string) => {
    analytics.trackEvent('faq_expand', {
      question: question,
      event_category: 'support',
    });
  },

  trackInsightView: (title: string, category: string) => {
    analytics.trackEvent('insight_view', {
      insight_title: title,
      insight_category: category,
      event_category: 'content',
    });
  },

  trackNewsletterSignup: (source: string) => {
    analytics.trackEvent('newsletter_signup', {
      source: source,
      event_category: 'engagement',
    });
  },

  trackAIAnalysisView: (page: string, occupation?: string) => {
    analytics.trackEvent('ai_analysis_view', {
      page_type: page,
      occupation_code: occupation,
      event_category: 'ai_features',
    });
  },

  trackAIAnalysisCTAClick: (source: string, occupation?: string, isPro?: boolean) => {
    analytics.trackEvent('ai_analysis_cta_click', {
      source: source,
      occupation_code: occupation,
      is_pro_user: isPro,
      event_category: 'ai_features',
    });
  },

  trackFreeReportRequest: (occupation: string, area: string, source: string) => {
    analytics.trackEvent('free_report_request', {
      occupation_code: occupation,
      area_code: area,
      source: source,
      event_category: 'lead_generation',
    });
  },

  trackAIProUpgrade: (source: string, occupation?: string) => {
    analytics.trackEvent('ai_pro_upgrade', {
      source: source,
      occupation_code: occupation,
      event_category: 'revenue',
      conversion_type: 'ai_analysis',
    });
  },
};

const trackPageView = (path: string, title?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

const cookieConsent = {
  hasConsent: (): boolean => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('wage_atlas_cookie_consent') === 'accepted';
  }
};

// Hook for page view tracking. Not consent-gated: gtag runs in Consent Mode v2,
// which sends cookieless pings until the user grants analytics_storage.
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);
};

// Hook for revenue analytics
export const useRevenueAnalytics = () => {
  return {
    trackAffiliate: analytics.trackAffiliateClick,
    trackSalaryLookup: analytics.trackSalaryLookup,
    trackRppToggle: analytics.trackRppToggle,
    trackCalculator: analytics.trackCalculatorUsage,
    trackSearch: analytics.trackSearch,
    trackNoDegreeView: analytics.trackNoDegreeView,
    trackExport: analytics.trackExport,
    trackAdView: analytics.trackAdView,
    trackPdfDownload: analytics.trackPdfDownload,
    trackCalcConvert: analytics.trackCalcConvert,
    trackTableSort: analytics.trackTableSort,
    trackNavInternal: analytics.trackNavInternal,
  };
};

// General analytics hook
export const useAnalytics = () => {
  return {
    trackEvent: analytics.trackEvent,
    trackContactFormSubmit: analytics.trackContactFormSubmit,
    trackFAQClick: analytics.trackFAQClick,
    trackFAQExpand: analytics.trackFAQExpand,
    trackInsightView: analytics.trackInsightView,
    trackNewsletterSignup: analytics.trackNewsletterSignup,
    trackAIAnalysisView: analytics.trackAIAnalysisView,
    trackAIAnalysisCTAClick: analytics.trackAIAnalysisCTAClick,
    trackFreeReportRequest: analytics.trackFreeReportRequest,
    trackAIProUpgrade: analytics.trackAIProUpgrade,
    ...useRevenueAnalytics(),
  };
};

// Custom hook for tracking component visibility (for ad viewability)
export const useViewTracking = (
  elementRef: React.RefObject<HTMLElement>,
  callback: () => void,
  threshold: number = 0.5
) => {
  useEffect(() => {
    if (!elementRef.current || !cookieConsent.hasConsent()) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          callback();
          observer.disconnect(); // Track only once
        }
      },
      { threshold }
    );

    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, [elementRef, callback, threshold]);
};